// src/components/tutor/schedule/TutorWeekBar.js
import React, { useEffect, useMemo, useState } from "react";

// Reuse the admin drawer CSS (drawer styles + mini-calendar classes)
import "../../admin/scheduling/components/WeekBar.css";
import "./TutorWeekBar.css";

import { DAYS_ORDER } from "../../admin/scheduling/constants";
import { FiMinus, FiPlus, FiX } from "react-icons/fi";

function clampToMidnight(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function isSameDay(a, b) {
  if (!a || !b) return false;
  return +clampToMidnight(a) === +clampToMidnight(b);
}
function isBetweenInclusive(d, a, b) {
  const t = +clampToMidnight(d);
  return t >= +clampToMidnight(a) && t <= +clampToMidnight(b);
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function startOfMonthLocal(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonthLocal(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function buildMonthGrid(focusMonth) {
  const start = startOfMonthLocal(focusMonth);
  const startDow = start.getDay();
  const gridStart = addDays(start, -startDow);
  const rows = [];
  let cur = gridStart;
  for (let r = 0; r < 6; r++) {
    const row = [];
    for (let c = 0; c < 7; c++) { row.push(cur); cur = addDays(cur, 1); }
    rows.push(row);
  }
  // Drop any row where every day falls outside the focus month (e.g. an all-next-month 6th week)
  const fm = focusMonth.getMonth();
  const fy = focusMonth.getFullYear();
  const filteredRows = rows.filter((row) =>
    row.some((d) => d.getMonth() === fm && d.getFullYear() === fy)
  );
  return { rows: filteredRows };
}
function monthLabel(d) {
  return new Intl.DateTimeFormat("en-US", { month: "long" }).format(d);
}
function yearLabel(d) {
  return new Intl.DateTimeFormat("en-US", { year: "numeric" }).format(d);
}

export default function TutorWeekBar({
  isOpen,
  onClose,

  weekStart,
  weekEnd,
  onPrevWeek,
  onNextWeek,
  onToday,

  uiZoom = 1,
  onUiZoomChange,

  viewMode,
  onSetViewMode,
  currentDayDate,
  onPrevDay,
  onNextDay,
  onPickDate,
  isDateClosed,
}) {
  const isDayView = DAYS_ORDER.includes(viewMode);
  const primaryView = isDayView ? "day" : "week";

  const selectedDate = useMemo(() => {
    if (primaryView === "day" && currentDayDate) return clampToMidnight(currentDayDate);
    return clampToMidnight(weekStart || new Date());
  }, [primaryView, currentDayDate, weekStart]);

  const headerMain = useMemo(() => {
    if (primaryView === "day" && currentDayDate) {
      return currentDayDate.toLocaleDateString("en-US", { weekday: "long" });
    }
    return (weekStart || new Date()).toLocaleDateString("en-US", { weekday: "long" });
  }, [primaryView, currentDayDate, weekStart]);

  const headerSub = useMemo(() => {
    if (primaryView === "day" && currentDayDate) {
      return currentDayDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    }
    const ws = weekStart || selectedDate;
    const we = addDays(ws, 6);
    return `${ws.toLocaleDateString("en-US", { month: "long", day: "numeric" })} – ${we.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`;
  }, [primaryView, currentDayDate, weekStart, selectedDate]);

  const navHandlers = useMemo(() => {
    if (primaryView === "day") return { onPrev: onPrevDay || (() => {}), onNext: onNextDay || (() => {}) };
    return { onPrev: onPrevWeek || (() => {}), onNext: onNextWeek || (() => {}) };
  }, [primaryView, onPrevDay, onNextDay, onPrevWeek, onNextWeek]);

  const handleSelectPrimary = (nextPrimary) => {
    if (!onSetViewMode) return;
    if (nextPrimary === "day") {
      if (isDayView) return;
      const todayMid = clampToMidnight(new Date());
      const ws = clampToMidnight(weekStart);
      const we = addDays(ws, 6);
      let candidateDate = isBetweenInclusive(todayMid, ws, we) ? todayMid : ws;
      const keys = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];
      let candidateKey = keys[candidateDate.getDay()];
      let attempts = 0;
      while ((isDateClosed && isDateClosed(candidateDate)) && attempts < 7) {
        candidateDate = addDays(candidateDate, 1);
        candidateKey = keys[candidateDate.getDay()];
        attempts += 1;
      }
      onSetViewMode(candidateKey);
      return;
    }
    onSetViewMode("week");
  };

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handle = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  }, [isOpen, onClose]);

  const [calMonth, setCalMonth] = useState(() => startOfMonthLocal(selectedDate));
  useEffect(() => {
    const a = selectedDate;
    if (a.getFullYear() !== calMonth.getFullYear() || a.getMonth() !== calMonth.getMonth()) {
      setCalMonth(startOfMonthLocal(a));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const cal = useMemo(() => buildMonthGrid(calMonth), [calMonth]);

  const weekHighlightStart = useMemo(() => {
    if (primaryView !== "week") return null;
    return clampToMidnight(weekStart || selectedDate);
  }, [primaryView, weekStart, selectedDate]);

  const weekHighlightEnd = useMemo(() => {
    if (!weekHighlightStart) return null;
    return addDays(weekHighlightStart, 6);
  }, [weekHighlightStart]);

  const zoomPct = Math.round((uiZoom || 1) * 100);

  const handlePickDate = (d) => {
    if (onPickDate) onPickDate(clampToMidnight(d));
  };

  return (
    <>
      <aside
        className={`schedDrawer ${isOpen ? "isOpen" : ""}`}
        aria-label="Tutor schedule sidebar"
      >
        <div className="sbScrollContent">
          {/* ── Header ── */}
          <div className="sbTop">
            <div className="sbDrawerHeadRow">
              <div className="sbDrawerTitles">
                <div className="sbWeekTitle">{headerMain}</div>
                {headerSub ? <div className="sbWeekRange">{headerSub}</div> : null}
              </div>
              <button type="button" className="sbCloseBtn" onClick={onClose} aria-label="Close sidebar">
                <FiX size={18} />
              </button>
            </div>

            {/* Nav row */}
            <div className="sbNavRow">
              <button type="button" className="sbCircleBtn" onClick={navHandlers.onPrev} aria-label="Previous">‹</button>
              <button type="button" className="sbTodayBtn" onClick={onToday}>Today</button>
              <button type="button" className="sbCircleBtn" onClick={navHandlers.onNext} aria-label="Next">›</button>
            </div>

            {/* Day / Week toggle */}
            <div className="sbSegmented" role="tablist" aria-label="View toggle">
              <button
                type="button"
                className={`sbSegBtn ${primaryView === "day" ? "isActive" : ""}`}
                onClick={() => handleSelectPrimary("day")}
              >
                Day
              </button>
              <button
                type="button"
                className={`sbSegBtn ${primaryView === "week" ? "isActive" : ""}`}
                onClick={() => handleSelectPrimary("week")}
              >
                Week
              </button>
            </div>
          </div>

          {/* ── Mini Calendar ── */}
          <div className="sbCalendarCard">
            <div className="sbCalHeader">
              <button
                type="button"
                className="sbCalNavBtn"
                onClick={() => setCalMonth((d) => startOfMonthLocal(addDays(d, -1)))}
                aria-label="Previous month"
              >‹</button>
              <div className="sbCalMonthLabel">
                <span className="sbCalMonth">{monthLabel(calMonth)}</span>
                <span className="sbCalYear">{yearLabel(calMonth)}</span>
              </div>
              <button
                type="button"
                className="sbCalNavBtn"
                onClick={() => setCalMonth((d) => startOfMonthLocal(addDays(endOfMonthLocal(d), 1)))}
                aria-label="Next month"
              >›</button>
            </div>

            <div className="sbCalDowRow">
              {["S","M","T","W","T","F","S"].map((d, i) => (
                <div key={i} className="sbCalDow">{d}</div>
              ))}
            </div>

            <div className="sbCalGrid">
              {cal.rows.flat().map((d, idx) => {
                const isClosed = isDateClosed ? !!isDateClosed(d) : (d.getDay() === 0 || d.getDay() === 5);
                const inMonth = d.getMonth() === calMonth.getMonth();
                const isSelected = primaryView === "day" && isSameDay(d, selectedDate);
                const inWeekRange = primaryView === "week" && weekHighlightStart &&
                  isBetweenInclusive(d, weekHighlightStart, weekHighlightEnd) && !isClosed;
                const isRangeStart = inWeekRange && isSameDay(d, weekHighlightStart);
                const isRangeEnd   = inWeekRange && isSameDay(d, weekHighlightEnd);

                const cls = [
                  "sbCalDay",
                  !inMonth ? "isOutside" : "",
                  isClosed ? "isClosed" : "",
                  isSelected ? "isSelected" : "",
                  inWeekRange ? "isInRange" : "",
                  isRangeStart ? "isRangeStart" : "",
                  isRangeEnd ? "isRangeEnd" : "",
                ].filter(Boolean).join(" ");

                return (
                  <button
                    key={`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${idx}`}
                    type="button"
                    className={cls}
                    disabled={isClosed}
                    onClick={() => !isClosed && handlePickDate(d)}
                    aria-label={d.toDateString()}
                  >
                    {d.getDate()}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Zoom controls ── */}
        <div className="tutorDrawerZoom">
          <span className="tutorDrawerZoomLabel">Zoom: {zoomPct}%</span>
          <div className="tutorDrawerZoomBtns">
            <button
              type="button"
              className="schedDropdownZoomBtn"
              onClick={() => onUiZoomChange?.(Number(Math.max(0.2, uiZoom - 0.1).toFixed(1)))}
              aria-label="Zoom out"
            >
              <FiMinus size={14} />
            </button>
            <button
              type="button"
              className="schedDropdownZoomBtn"
              onClick={() => onUiZoomChange?.(Number(Math.min(3.0, uiZoom + 0.1).toFixed(1)))}
              aria-label="Zoom in"
            >
              <FiPlus size={14} />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
